import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import {
  IconCaretDown,
  IconCaretUp,
  IconChartArea,
  IconMinus,
} from "@tabler/icons-react";
import { groupBy, last, map, orderBy, sumBy } from "lodash";
import { useCallback } from "react";
import Area from "../../util/charts/area";
import { SliceTooltipProps } from "@nivo/line";
import MoedaText from "../../util/ui/inputs/moedaText";
import moment from "moment";
import PercentualText from "../../util/ui/inputs/percentualText";
import Functions from "../../../callable/functions";
import Patrimonio from "../../../models/analises/patrimonio";
import FelixCard from "../../util/ui/felixCard";
import ChartContainer from "../../util/charts/chartContainer";
import usePromise from "../../util/usePromise";
import { felixTokens, radius } from "../../../theme";

export default function CardPatrimonio() {
  const theme = useTheme();
  const obterPatrimonioPromise = useCallback(
    () =>
      new Functions()
        .obterPatrimonio()
        .then((res) => map(res.data, Patrimonio.fromJson)),
    []
  );
  const { data: patrimonio = [], loading } = usePromise(obterPatrimonioPromise);

  const patrimonioPorMes = groupBy(patrimonio, (p) => p.data.format("YYYY-MM"));

  const data = [
    {
      id: "Valor Aplicado",
      // Séries em tokens da marca: lavanda (accent-2) x indigo como tinta.
      // `primary.dark` sumia sobre a superfície escura do tema dark.
      color: theme.palette.tertiary.main,
      data: map(patrimonioPorMes, (patrimoniosMes, x) => {
        const patrimonioPorDia = groupBy(patrimoniosMes, (p) =>
          p.data.format("YYYY-MM-DD")
        );
        const ultimoDiaDoMes =
          last(orderBy(patrimoniosMes, (p) => p.data.unix()))?.data ?? moment();
        return {
          x: ultimoDiaDoMes.format("YYYY-MM-DD"),
          y: sumBy(
            patrimonioPorDia[ultimoDiaDoMes.format("YYYY-MM-DD")],
            "valorAplicado"
          ),
        };
      }),
    },
    {
      id: "Saldo Bruto",
      color: theme.palette.accentInk,
      data: map(patrimonioPorMes, (patrimoniosMes, x) => {
        const patrimonioPorDia = groupBy(patrimoniosMes, (p) =>
          p.data.format("YYYY-MM-DD")
        );
        const ultimoDiaDoMes =
          last(orderBy(patrimoniosMes, (p) => p.data.unix()))?.data ?? moment();
        return {
          x: ultimoDiaDoMes.format("YYYY-MM-DD"),
          y: sumBy(
            patrimonioPorDia[ultimoDiaDoMes.format("YYYY-MM-DD")],
            "saldoBruto"
          ),
        };
      }),
    },
  ];

  const formatTooltip = ({ slice }: SliceTooltipProps) => {
    const variacao =
      !slice.points[1] ||
      isNaN(
        (slice.points[0].data.y as number) /
          (slice.points[1].data.y as number) -
          1
      )
        ? 0
        : (slice.points[0].data.y as number) /
            (slice.points[1].data.y as number) -
          1;
    const corVariacao =
      variacao > 0
        ? theme.palette.success.main
        : variacao < 0
        ? theme.palette.error.main
        : theme.palette.text.secondary;
    const IconeVariacao =
      variacao > 0 ? IconCaretUp : variacao < 0 ? IconCaretDown : IconMinus;
    return (
      // Tooltip de gráfico: surface + hairline vindos do Card do tema.
      <Card
        elevation={0}
        sx={{ boxShadow: felixTokens.shadow.overlay, borderRadius: radius.sm }}
      >
        <Box display="flex" flexDirection="column" padding={1.5} minWidth={200}>
          <Typography variant="labelMono" color="text.secondary" gutterBottom>
            {moment(slice.points[0].data.x).format("D [de] MMMM [de] YYYY")}
          </Typography>
          {map(slice.points, (point) => {
            return (
              <Box
                key={point.id}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  paddingRight={1}
                >
                  <Box
                    width={8}
                    height={8}
                    component="span"
                    borderRadius={radius.round}
                    marginRight={1}
                    style={{ backgroundColor: point.serieColor }}
                  />
                  <Typography variant="labelMono" color="text.secondary">
                    {point.serieId}
                  </Typography>
                </Box>
                <Typography variant="dataMono" color="text.primary">
                  {<MoedaText value={point.data.y} />}
                </Typography>
              </Box>
            );
          })}
          <Box
            key="variacao"
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            paddingTop={0.5}
          >
            <IconeVariacao size={12} strokeWidth={3} color={corVariacao} />
            <Typography
              variant="dataMono"
              sx={{ fontWeight: 500, color: corVariacao }}
            >
              {<PercentualText value={variacao} decimalScale={2} />}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <FelixCard>
      <CardHeader title="Patrimônio" avatar={<IconChartArea size={16} />} />
      <CardContent>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={patrimonio.length === 0}
          isLoading={loading}
        >
          {loading ? (
            // Spinner é traço, não preenchimento: `info` é o canal do
            // accent-ink. Em `primary.main` o anel dá 2,57:1 no dark.
            <CircularProgress color="info" />
          ) : (
            <Area data={data} tooltip={formatTooltip} />
          )}
        </ChartContainer>
      </CardContent>
    </FelixCard>
  );
}
